import type { ChatBotEntity } from '@common/types/chat-bot.type';
import type { ChatBotChainEntity } from '@common/types/chat-bot-chain.type';

import { ChangeDetectionStrategy, Component, computed, effect, inject, input, output, signal } from '@angular/core';
import { AsyncPipe, TitleCasePipe } from '@angular/common';
import { BsDropdownModule } from 'ngx-bootstrap/dropdown';
import { type Observable, of } from 'rxjs';
import { filter, take } from 'rxjs/operators';

import { MESSENGER_BOT_LINK_BASE, chatBotsUrl } from '@common/constants/chat-bots.consts';
import { EChatBotsMessengers } from '@common/enums/chat-bots.enum';
import { ESocialNetworks } from '@common/enums';

import type { FormChatBotData } from '@sites/entity-core';
import type { FormWidgetForm } from '@sites/entity-signal-form';

import { ChatBotsCollectionFacade } from '@web/store/collections/chat-bots/chat-bots.facade';
import { ChatBotChainsCollectionFacade } from '@web/store/collections/chat-bot-chains/chat-bot-chains.facade';
import { AppFacade } from '@web/store/app/app.facade';
import { CommonElementsModule } from '@web/widgets/common/common-elements.module';

import { FormSettingsPanel } from '@web/widgets/common/abstract/form-settings-panel';

/**
 * Chat-bot settings panel for the subscription-form widget — value-in
 * (`FormChatBotData`) / `valueChangeEvent`-out, with facade-driven flows
 * (bot list per messenger, chain loading, default-chain pick) and
 * per-messenger deep-link generation. The parent writes the emitted values
 * into `form`; save/cancel and the transaction frame around those writes come
 * from `FormSettingsPanel`. The chat-bot widget has its own FormGroup-based
 * `web-chat-bot-settings`.
 */
@Component({
    selector: 'web-form-chat-bot-settings',
    templateUrl: './form-chat-bot-settings.component.html',
    styleUrls: ['../../../../chat-bot/components/chat-bot-control/chat-bot-settings/chat-bot-settings.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CommonElementsModule, BsDropdownModule, AsyncPipe, TitleCasePipe],
})
export class FormChatBotSettingsComponent extends FormSettingsPanel<FormWidgetForm> {
    public readonly chatBot = input<FormChatBotData | null>(null);
    public readonly chatBotsList = input<ChatBotEntity[]>([]);
    public readonly isSubscriptionForm = input<boolean>(false);
    public readonly selectedChatbots = input<FormChatBotData[]>([]);
    public readonly existingChatBots = input<Partial<Record<string, string>>>({});

    public readonly valueChangeEvent = output<FormChatBotData>();

    private readonly chatBotsCollectionFacade: ChatBotsCollectionFacade = inject(ChatBotsCollectionFacade);
    private readonly chatBotChainsCollectionFacade: ChatBotChainsCollectionFacade =
        inject(ChatBotChainsCollectionFacade);
    private readonly appFacade: AppFacade = inject(AppFacade);

    protected readonly ESocialNetworks = ESocialNetworks;

    private readonly model = signal<FormChatBotData | null>(null);
    private chatBotsMap: Record<string, ChatBotEntity> = {};

    public readonly isChainsLoading = signal(false);

    public readonly socialNetwork = computed(() => this.model()?.socialNetwork);
    public readonly iconType = computed(() => this.model()?.iconType);
    public readonly name = computed(() => this.model()?.name ?? '');
    public readonly link = computed(() => this.model()?.link ?? '');
    public readonly botId = computed(() => this.model()?.botId ?? null);
    public readonly botName = computed(() => this.model()?.botName ?? null);
    public readonly chainName = computed(() => this.model()?.botChain?.name ?? null);
    public readonly preventLinkConverting = computed(() => Boolean(this.model()?.preventLinkConverting));

    public readonly messengerNames = computed<ESocialNetworks[]>(() => {
        const forbiddenMessengers = this.isSubscriptionForm()
            ? [EChatBotsMessengers.vk, EChatBotsMessengers.ok, EChatBotsMessengers.tiktok]
            : [EChatBotsMessengers.vk, EChatBotsMessengers.ok];

        const sourceKeys = this.isSubscriptionForm()
            ? Object.keys(this.existingChatBots())
            : Object.keys(EChatBotsMessengers);

        const selectedNetworks = new Set(
            this.selectedChatbots()
                .map((item) => item.socialNetwork)
                .filter((network) => network !== this.socialNetwork())
        );

        return sourceKeys.filter(
            (key) =>
                !forbiddenMessengers.includes(key as EChatBotsMessengers) &&
                !selectedNetworks.has(key as ESocialNetworks)
        ) as ESocialNetworks[];
    });

    constructor() {
        super();

        effect(() => {
            const incoming = this.chatBot();
            if (!incoming) return;
            this.model.set(incoming);
        });

        // Mirror of the FormGroup original's ngOnChanges: rebuild the bot map
        // when the list arrives; drop a selected bot that vanished from it.
        effect(() => {
            const list = this.chatBotsList();
            if (!list) return;

            this.chatBotsMap = list.reduce((acc, item) => {
                acc[item.id.toString()] = item;
                return acc;
            }, {});

            const botId = this.model()?.botId;
            if (botId && !list.some((item) => item.id.toString() === botId)) {
                this.patch({
                    botId: null,
                    botName: null,
                    botChain: { chainId: null, name: null, isDefault: false },
                    uri: null,
                    phone: null,
                });
            }
        });
    }

    // --- Messenger / bot / chain flows ---

    public setMessenger(messengerType: ESocialNetworks): void {
        this.appFacade
            .isAdminCollectionWrapper$(this.chatBotsCollectionFacade.chatBots$(messengerType))
            .pipe(
                filter((chatBots) => !!chatBots),
                take(1)
            )
            .subscribe((chatBots) => {
                this.chatBotsMap = chatBots.reduce((acc, item) => {
                    acc[item.id.toString()] = item;
                    return acc;
                }, {});

                if (chatBots.length) {
                    const current = chatBots[0];
                    this.patch({
                        socialNetwork: messengerType,
                        name: messengerType,
                        botName: current.name,
                        botId: current.id.toString(),
                        uri: current?.uri ?? null,
                        phone: current?.phone ?? null,
                    });
                    this.setDefaultChain(messengerType, current);
                    return;
                }

                this.patch({
                    socialNetwork: messengerType,
                    name: messengerType,
                    link: null,
                    botName: null,
                    botId: null,
                    botChain: { chainId: null, name: null, isDefault: false },
                    uri: null,
                    phone: null,
                });
            });
    }

    public setBot(bot: ChatBotEntity): void {
        this.isChainsLoading.set(true);
        this.chatBotChainsCollectionFacade
            .chatBotChains$(this.socialNetwork(), bot.externalId)
            .pipe(
                filter((chatBotChains) => Boolean(chatBotChains)),
                take(1)
            )
            .subscribe((chatBotChains) => {
                this.isChainsLoading.set(false);

                this.patch({
                    botName: bot.name,
                    botId: bot.id.toString(),
                    uri: bot?.uri ?? null,
                    phone: bot?.phone ?? null,
                });

                if (chatBotChains.length) {
                    const defaultChain = chatBotChains.find((chain) => chain.isDefault);
                    this.setBotChain(defaultChain ?? chatBotChains[0]);
                    return;
                }

                this.patch({ botChain: { chainId: null, name: null, isDefault: false } });
            });
    }

    private setDefaultChain(messengerType: string, bot: ChatBotEntity): void {
        this.chatBotChainsCollectionFacade
            .chatBotChains$(messengerType, bot.externalId)
            .pipe(
                filter((chains) => Boolean(chains)),
                take(1)
            )
            .subscribe((chains) => {
                const defaultChain = chains.find((chain) => chain.isDefault);
                this.setBotChain(defaultChain ?? chains[0]);
            });
    }

    public setBotChain(value: ChatBotChainEntity): void {
        this.patch({ botChain: { chainId: value.chainId, name: value.name, isDefault: value.isDefault } });
        this.setLink();
    }

    public useOwnLink(): void {
        this.patch({
            link: null,
            botId: null,
            botName: null,
            botChain: { chainId: null, name: null, isDefault: false },
            uri: null,
            phone: null,
            preventLinkConverting: false,
        });
    }

    private setLink(): void {
        const model = this.model();
        if (!model) return;

        const host = chatBotsUrl[model.socialNetwork]?.[0];
        const selectedChatBot = this.getCurrentChatBot();

        if (!selectedChatBot) {
            return;
        }

        const chainId = model.botChain?.chainId;

        switch (model.socialNetwork) {
            case ESocialNetworks.instagram:
                this.patch({ link: `https://ig.me/m/${model.botId}?ref=${chainId}` });
                break;
            case ESocialNetworks.messenger:
                // Built on `messenger.com/t`, not the `m.me` shortener Meta is retiring (#73155).
                this.patch({ link: `${MESSENGER_BOT_LINK_BASE}/${model.botId}?ref=${chainId}` });
                break;
            case ESocialNetworks.telegram:
                this.patch({ link: `${host}/${selectedChatBot.name}?start=${chainId}` });
                break;
            case ESocialNetworks.viber:
                this.patch({ link: `viber://pa?chatURI=${model.uri}&context=${chainId}` });
                break;
            case ESocialNetworks.whatsapp:
                this.patch({ link: `${host}/${model.phone}?start=${chainId}&text=Start` });
                break;
            case ESocialNetworks.tiktok:
                this.patch({ link: `${host}/${selectedChatBot.name.toLowerCase()}?ref=${chainId}` });
                break;
            default:
                this.patch({ link: '' });
        }
    }

    private getCurrentChatBot(): ChatBotEntity | null {
        const botId = this.model()?.botId;
        if (botId && this.chatBotsMap[botId.toString()]) {
            return this.chatBotsMap[botId.toString()];
        }
        return null;
    }

    // --- Simple field handlers ---

    public onNameChange(event: Event): void {
        this.patch({ name: (event.target as HTMLInputElement).value });
    }

    public onLinkChange(event: Event): void {
        this.patch({ link: (event.target as HTMLInputElement).value });
    }

    public togglePreventLinkConverting(event: Event): void {
        this.patch({ preventLinkConverting: (event.target as HTMLInputElement).checked });
    }

    public getPlaceholder(social: string): string {
        switch (social) {
            case EChatBotsMessengers.email:
                return 'example@mail.com';
            case EChatBotsMessengers.phone:
                return '+1 234 567 89 10';
            default:
                return 'https://site.com';
        }
    }

    public get chatBotChains$(): Observable<ChatBotChainEntity[]> {
        const model = this.model();
        if (!model?.socialNetwork || !model?.botId) {
            return of([]);
        }

        const selectedChatBot = this.getCurrentChatBot();
        if (!selectedChatBot) {
            return of([]);
        }

        return this.chatBotChainsCollectionFacade.chatBotChains$(model.socialNetwork, selectedChatBot.externalId);
    }

    private patch(patch: Partial<FormChatBotData>): void {
        const current = this.model();
        if (!current) return;
        const next = { ...current, ...patch };
        this.model.set(next);
        this.valueChangeEvent.emit(next);
    }
}
